// app/frontend/components/inbox/LiveTranslateHeader.tsx

import { Languages } from 'lucide-react'
import { getLanguageFlag } from '@/lib/languages'

interface LiveTranslateHeaderProps {
  /** The detected/confirmed language code for this conversation */
  detectedLanguage: string | null
  /** Human readable name of the detected language */
  detectedLanguageName: string | null
  /** Whether live translate is currently enabled */
  liveTranslateEnabled: boolean
  /** Language detected from messages (may differ from confirmed) */
  autoDetectedLanguage: string | null
  /** Human readable name of auto-detected language */
  autoDetectedLanguageName: string | null
  /** Called when user clicks to toggle live translate */
  onToggle: () => void
  /** Whether the toggle action is in progress */
  isLoading?: boolean
}

export function LiveTranslateHeader({
  detectedLanguage,
  detectedLanguageName,
  liveTranslateEnabled,
  autoDetectedLanguage,
  autoDetectedLanguageName,
  onToggle,
  isLoading = false
}: LiveTranslateHeaderProps) {
  // Use confirmed language if enabled, otherwise show auto-detected
  const displayLanguage = liveTranslateEnabled ? detectedLanguage : autoDetectedLanguage
  const displayLanguageName = liveTranslateEnabled ? detectedLanguageName : autoDetectedLanguageName

  // Don't show anything if no language detected
  if (!displayLanguage) {
    return null
  }

  const flag = getLanguageFlag(displayLanguage)

  if (liveTranslateEnabled) {
    // Active state - show flag and "LiveTranslate On" with option to stop
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-2 bg-primary/10 border-b border-primary/20">
        <span className="text-lg">{flag}</span>
        <span className="text-sm font-medium text-primary">
          {displayLanguageName}
        </span>
        <span className="text-xs text-muted-foreground">·</span>
        <div className="flex items-center gap-1.5 text-sm text-primary">
          <Languages className="size-4" />
          <span>LiveTranslate On</span>
        </div>
        <button
          onClick={onToggle}
          disabled={isLoading}
          className="ml-2 text-xs text-muted-foreground hover:text-foreground underline cursor-pointer disabled:opacity-50"
        >
          {isLoading ? 'Stopping...' : 'Stop'}
        </button>
      </div>
    )
  }

  // Inactive state - show flag and "Start LiveTranslate" link
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
      <span className="text-lg">{flag}</span>
      <span className="text-sm text-muted-foreground">
        {displayLanguageName}
      </span>
      <button
        onClick={onToggle}
        disabled={isLoading}
        className="text-sm text-primary hover:text-primary/80 hover:underline cursor-pointer disabled:opacity-50"
      >
        {isLoading ? 'Starting...' : 'Start LiveTranslate'}
      </button>
    </div>
  )
}

export default LiveTranslateHeader
